import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, Check } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '@context/CartContext';
import { useAuth } from '@context/AuthContext';
import { orderAPI } from '@services/api';
import { formatCurrency } from '@utils/helpers';
import { ROUTES, STRIPE_PUBLISHABLE_KEY } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import CartSummary from '@components/cart/CartSummary';
import Alert from '@components/common/Alert';
import type { CheckoutForm } from '@/types';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY || '');

const CheckoutPage: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutContent />
    </Elements>
  );
};

const CheckoutContent: React.FC = () => {
  const [checkoutData, setCheckoutData] = useState<CheckoutForm>({
    email: '',
    name: '',
    phone: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);

  const { items, total, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  // Pre-fill form with user data if authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Compute display name from available fields
      const displayName = user.name ||
        (user.first_name || user.last_name
          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
          : '');
      setCheckoutData(prev => ({
        ...prev,
        email: user.email,
        name: displayName,
      }));
    }
  }, [isAuthenticated, user]);

  // Create payment intent when component mounts
  useEffect(() => {
    if (items.length === 0) {
      navigate(ROUTES.CART);
      return;
    }

    const createPaymentIntent = async () => {
      try {
        const response = await orderAPI.createPaymentIntent({
          items: items.map(item => ({
            photoId: item.photoId,
            resolution: item.resolution,
            quantity: item.quantity,
          })),
        });

        setClientSecret(response.data.clientSecret);
        setOrderId(response.data.orderId);
      } catch (error: any) {
        setError(error.message || 'Failed to initialize checkout');
      }
    };

    createPaymentIntent();
  }, [items, navigate]);

  const handleInputChange = (field: keyof CheckoutForm, value: string) => {
    setCheckoutData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!checkoutData.email || !checkoutData.name) {
      setError('Please fill in all required fields');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(checkoutData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    if (!validateForm()) return;

    setIsProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm payment
      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: checkoutData.name,
            email: checkoutData.email,
            phone: checkoutData.phone,
          },
        },
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm order on backend
        if (orderId) {
          await orderAPI.confirmOrder(orderId, paymentIntent.id);
        }

        // Clear cart and redirect to success page
        clearCart();
        navigate(`${ROUTES.ORDER_SUCCESS}?order_id=${orderId}&payment_intent=${paymentIntent.id}`);
      }
    } catch (error: any) {
      setError(error.message || 'Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  if (items.length === 0) {
    return null; // Will redirect to cart
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.CART)}
            icon={<ArrowLeft size={16} />}
            className="text-gray-600 hover:text-gray-900 mb-4"
          >
            Back to Cart
          </Button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Checkout</h1>
          <p className="text-gray-600">Complete your purchase securely</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div className="space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Customer Information</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  label="Email Address"
                  value={checkoutData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  required
                  disabled={isAuthenticated}
                />

                <Input
                  type="text"
                  label="Full Name"
                  value={checkoutData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  required
                />

                <Input
                  type="tel"
                  label="Phone Number (Optional)"
                  value={checkoutData.phone || ''}
                  onChange={e => handleInputChange('phone', e.target.value)}
                />
              </form>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Information</h2>
                <div className="flex items-center gap-1 ml-2">
                  <Lock size={14} className="text-green-600" />
                  <span className="text-xs text-green-600">Secure</span>
                </div>
              </div>

              {clientSecret && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Information
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 bg-white">
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  </div>

                  {error && (
                    <Alert
                      type="error"
                      message={error}
                      dismissible
                      onDismiss={() => setError(null)}
                    />
                  )}

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Your payment is secure
                      </span>
                    </div>
                    <div className="text-xs text-green-700 space-y-1">
                      <div>• 256-bit SSL encryption</div>
                      <div>• PCI DSS Level 1 compliant</div>
                      <div>• Your card information is never stored</div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    className="w-full"
                    onClick={() => handleSubmit({} as React.FormEvent)}
                    loading={isProcessing}
                    disabled={!stripe || !elements || isProcessing}
                    icon={<CreditCard size={20} />}
                  >
                    {isProcessing ? 'Processing...' : `Pay ${formatCurrency(total)}`}
                  </Button>
                </div>
              )}
            </div>

            {/* Terms and conditions */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  By completing your purchase, you agree to our{' '}
                  <Link to={ROUTES.TERMS_OF_SERVICE} className="text-primary-600 hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to={ROUTES.PRIVACY_POLICY} className="text-primary-600 hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
                <p>Digital downloads will be available immediately after payment confirmation.</p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            {/* Order details */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Order Summary</h2>
              </div>

              <div className="space-y-4">
                {items.map(item => (
                  <div key={item.id} className="flex gap-3">
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 text-sm">{item.title}</h3>
                      <p className="text-xs text-gray-500">
                        {item.resolution} resolution • Qty: {item.quantity}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart summary */}
            <CartSummary showCheckoutButton={false} />

            {/* What you get */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-800 mb-3">What you'll receive:</h3>
              <div className="space-y-2 text-sm text-blue-700">
                <div className="flex items-center gap-2">
                  <Check size={14} />
                  <span>Instant download links via email</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} />
                  <span>High-resolution image files</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} />
                  <span>Lifetime access to downloads</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={14} />
                  <span>Commercial usage rights included</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;
